/**
 * What a customer may type into a UV insert text field.
 *
 * The print process accepts a restricted character set, so anything outside it is stripped as the
 * customer types rather than rejected later. The class below is the one `VisualiserTextBox` has
 * always applied, moved here verbatim so the card face inputs (message, caption) and the keepsake
 * card fields below the card enforce exactly the same rule. Do not "tidy" it —
 * `text-input-rules.test.ts` pins its source string for that reason.
 */
export const UV_INSERT_DISALLOWED_CHARS_REGEX = /[^A-Za-z0-9!@#$%^&*()_+\-=?.,:'"£¢`\s]/g

export type SanitiseUvInsertTextOptions = {
  value: string
  /** Maximum characters kept, counted **after** disallowed characters are stripped. */
  maxLength: number
  /** Maximum hard (`\n`) lines kept. Wrapped lines are not counted. Omit for no cap. */
  maxLines?: number
  /**
   * Print wrap width. When set together with `maxLines`, trailing characters are dropped until the
   * message wraps to no more than `maxLines` lines at this width (see {@link wrapUvInsertTextByChars}).
   */
  maxCharsPerLine?: number
}

export type WrapUvInsertTextByCharsOptions = {
  text: string
  /** Maximum characters on one wrapped line. */
  maxCharsPerLine: number
}

/**
 * Wraps text by character count: split on `\n`, then greedy-wrap each hard line on spaces at
 * `maxCharsPerLine`. A word longer than the limit is split at the limit, and an empty hard line
 * counts as one line.
 *
 * This is the print rule shared with Pulse, not a visual measure: it says how many lines the
 * message will occupy on the printed card, whatever width the browser draws the text at.
 *
 * @param options - Options object.
 * @param options.text - The message, with `\n` hard line breaks.
 * @param options.maxCharsPerLine - Maximum characters on one wrapped line.
 * @returns The wrapped lines, in order.
 */
export function wrapUvInsertTextByChars({ text, maxCharsPerLine }: WrapUvInsertTextByCharsOptions): string[] {
  const wrapped: string[] = []

  for (const hardLine of text.split("\n")) {
    let current = ""
    let hasLine = false

    for (const word of hardLine.split(" ")) {
      if (word.length > maxCharsPerLine) {
        if (hasLine) wrapped.push(current)
        let rest = word
        while (rest.length > maxCharsPerLine) {
          wrapped.push(rest.slice(0, maxCharsPerLine))
          rest = rest.slice(maxCharsPerLine)
        }
        current = rest
        hasLine = true
      } else if (!hasLine) {
        current = word
        hasLine = true
      } else if (current.length + 1 + word.length <= maxCharsPerLine) {
        current = `${current} ${word}`
      } else {
        wrapped.push(current)
        current = word
      }
    }

    wrapped.push(current)
  }

  return wrapped
}

/**
 * The value to commit for a UV insert text field: disallowed characters removed, then cut to
 * `maxLength`, then cut to `maxLines` hard lines, then (when `maxCharsPerLine` is set) trimmed
 * until the wrapped message fits in `maxLines` lines. The card face and the fields below it both use
 * this, so the two can never disagree about what a customer may type.
 *
 * Stripping before slicing means a pasted emoji cannot consume the customer's character budget.
 *
 * @param options - Options object.
 * @param options.value - The raw value from the input.
 * @param options.maxLength - Maximum characters to keep.
 * @param options.maxLines - Maximum hard lines to keep, if any.
 * @param options.maxCharsPerLine - Print wrap width, if the field wraps by character count.
 * @returns The sanitised value.
 */
export function sanitiseUvInsertText({
  value,
  maxLength,
  maxLines,
  maxCharsPerLine,
}: SanitiseUvInsertTextOptions): string {
  const cut = value.replace(UV_INSERT_DISALLOWED_CHARS_REGEX, "").slice(0, maxLength)
  if (!maxLines) return cut
  const lines = cut.split("\n")
  const capped = lines.length > maxLines ? lines.slice(0, maxLines).join("\n") : cut
  if (!maxCharsPerLine) return capped

  let fitted = capped
  while (fitted && wrapUvInsertTextByChars({ text: fitted, maxCharsPerLine }).length > maxLines) {
    fitted = fitted.slice(0, -1)
  }
  return fitted
}

export type ApplyUvInsertTextEditOptions = Omit<SanitiseUvInsertTextOptions, "value"> & {
  /** The committed value before this edit. */
  previous: string
  /** The textarea's value after the keystroke or paste, before any rule is applied. */
  raw: string
}

/**
 * The value to commit for one edit of a UV insert text field. Sanitises `raw`, except that an
 * insertion in the middle of the text which would push the text past a limit is rejected (the
 * previous value is kept), because cutting would silently delete the end of the customer's
 * message. Appends and pastes at the end, and every deletion, are sanitised as before.
 *
 * Characters removed for being disallowed do not count as a cut: only `maxLength`, `maxLines` and
 * the wrapped line count do.
 *
 * @param options - Options object.
 * @param options.previous - The committed value before the edit.
 * @param options.raw - The raw value from the input after the edit.
 * @param options.maxLength - Maximum characters to keep.
 * @param options.maxLines - Maximum hard lines to keep, if any.
 * @param options.maxCharsPerLine - Print wrap width, if the field wraps by character count.
 * @returns The value to commit; `previous` when a mid-message insertion was rejected.
 */
export function applyUvInsertTextEdit({
  previous,
  raw,
  ...rules
}: ApplyUvInsertTextEditOptions): string {
  const sanitised = sanitiseUvInsertText({ value: raw, ...rules })
  const stripped = raw.replace(UV_INSERT_DISALLOWED_CHARS_REGEX, "")
  const wasCut = sanitised !== stripped
  if (wasCut && raw.length > previous.length && !raw.startsWith(previous)) return previous
  return sanitised
}
